/**
 * D6BattleStepWidget - Wrapper pour les étapes de combat D6
 * Ajoute un bouton "ATTAQUER" qui ouvre l'arène de combat en modal
 * Inspiré de d6_battle_step_widget.dart en Flutter
 */

import React, {useState} from 'react';
import {View, Text, StyleSheet, TouchableOpacity, Modal, Platform} from 'react-native';
import {PlayerStep} from '../../../models/PlayerStep';
import {PlayerAction} from '../../../models/PlayerAction';
import {PlayerPath} from '../../../models/PlayerPath';
import BaseStepWidget from '../../../components/BaseStepWidget';
import D6BattleWidget from './D6BattleWidget';
import {usePlayer} from '../../../contexts/PlayerContext';
import GameStateHelper from '../../../helpers/GameStateHelper';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {faBolt} from '@fortawesome/free-solid-svg-icons';

interface D6BattleStepWidgetProps {
  step: PlayerStep;
}

const D6BattleStepWidget: React.FC<D6BattleStepWidgetProps> = ({step}) => {
  const {gameState, loadCurrentStep} = usePlayer();
  const [showCombatModal, setShowCombatModal] = useState(false);

  const handleOpenCombat = () => {
    setShowCombatModal(true);
  };

  const handleCloseCombat = async () => {
    setShowCombatModal(false);
    
    // Recharger l'étape pour :
    // 1. Si l'adversaire est vaincu : afficher les chemins conditionnels
    // 2. Si le joueur est à 0 PV (gameState FAILED) : afficher le FailedStepWidget
    if (gameState) {
      let shouldReload = false;
      
      // Vérifier directement les PV du joueur
      const playerLifeField = GameStateHelper.getAdventureExtraField(gameState, 'D6_PV');
      if (playerLifeField) {
        const playerLife = typeof playerLifeField.currentValue === 'number' 
          ? playerLifeField.currentValue 
          : parseInt(String(playerLifeField.currentValue || '0'), 10);
        if (playerLife <= 0) {
          shouldReload = true;
          console.log('[D6BattleStepWidget] Player has 0 HP, reloading step to show FailedStepWidget');
        }
      }
      
      // Vérifier si le gameState est en FAILED
      if (gameState.state === 'FAILED') {
        shouldReload = true;
        console.log('[D6BattleStepWidget] GameState is FAILED, reloading step to show FailedStepWidget');
      }
      
      // Vérifier si l'adversaire est vaincu
      const enemyLifeField = GameStateHelper.getStepExtraField(gameState, step.numero, 'D6_PV_MONSTER');
      if (enemyLifeField) {
        const enemyLife = typeof enemyLifeField.currentValue === 'number'
          ? enemyLifeField.currentValue
          : parseInt(String(enemyLifeField.currentValue || '0'), 10);
        if (enemyLife <= 0) {
          shouldReload = true;
          console.log('[D6BattleStepWidget] Enemy is defeated, reloading step to show paths');
        }
      }
      
      if (shouldReload) {
        // Attendre un peu pour s'assurer que le gameState est bien mis à jour
        await new Promise((resolve) => setTimeout(resolve, 100));
        await loadCurrentStep();
      }
    }
  };

  // Wrapper pour les actions : ajouter le bouton "ATTAQUER" avant les autres actions
  const buildActionsWithAttack = (context: any, actions: PlayerAction[]) => {
    return (
      <View>
        {/* Bouton pour ouvrir le combat en modal */}
        <View style={styles.attackButtonContainer}>
          <TouchableOpacity
            style={styles.attackButton}
            onPress={handleOpenCombat}
            activeOpacity={0.85}>
            <FontAwesomeIcon icon={faBolt} size={20} color="#fff" style={{marginRight: 12}} />
            <Text style={styles.attackButtonText}>ATTAQUER</Text>
          </TouchableOpacity>
        </View>
        
        {/* Actions normales de l'étape */}
        {actions.length > 0 && (
          <View style={styles.actionsContainer}>
            {actions.map((action, index) => (
              <TouchableOpacity
                key={action.id || index}
                style={styles.actionButton}
                onPress={() => {
                  // TODO: Gérer l'action
                  console.log('[D6BattleStepWidget] Action pressed:', action);
                }}>
                <Text style={styles.actionText}>{action.title || action.id}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <BaseStepWidget step={step} />
      
      {/* Bouton pour ouvrir le combat en modal - Positionné après le contenu de BaseStepWidget */}
      <View style={styles.attackButtonContainer}>
        <TouchableOpacity
          style={styles.attackButton}
          onPress={handleOpenCombat}
          activeOpacity={0.85}>
          <FontAwesomeIcon icon={faBolt} size={20} color="#fff" style={{marginRight: 12}} />
          <Text style={styles.attackButtonText}>ATTAQUER</Text>
        </TouchableOpacity>
      </View>

      {/* Modal de combat fullscreen */}
      <Modal
        visible={showCombatModal}
        animationType="fade"
        transparent={false}
        onRequestClose={() => {
          // Ne rien faire sur ESC/back - l'utilisateur doit cliquer sur Fuir/Fermer
          console.log('[D6BattleStepWidget] Modal onRequestClose - ignored');
        }}>
        <D6BattleWidget step={step} onFlee={handleCloseCombat} />
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  attackButtonContainer: {
    padding: 16,
    paddingTop: 0,
    backgroundColor: 'transparent',
  },
  attackButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#D32F2F', // Rouge vif pour l'action de combat
    paddingVertical: 20,
    paddingHorizontal: 32,
    ...Platform.select({
      web: {
        borderTopLeftRadius: 14,
        borderTopRightRadius: 10,
        borderBottomRightRadius: 16,
        borderBottomLeftRadius: 12,
      },
      default: {
        borderRadius: 16,
      },
    }),
    minHeight: 44,
    ...Platform.select({
      web: {
        boxShadow: '0 4px 12px rgba(211, 47, 47, 0.4)',
      },
      default: {
        shadowColor: '#D32F2F',
        shadowOffset: {width: 0, height: 4},
        shadowOpacity: 0.4,
        shadowRadius: 8,
        elevation: 6,
      },
    }),
  },
  attackButtonText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFFFFF',
    letterSpacing: 1.2,
  },
  actionsContainer: {
    marginTop: 16,
  },
  actionButton: {
    backgroundColor: '#E8D5C4',
    padding: 18,
    borderRadius: 0,
    marginBottom: 16,
    borderLeftWidth: 3,
    borderLeftColor: '#8B7355',
  },
  actionText: {
    fontSize: 17,
    fontWeight: '400',
    color: '#2C2C2C',
  },
});

export default D6BattleStepWidget;
